import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import ComputerIcon from "@mui/icons-material/Computer";
import Diversity3Rounded from "@mui/icons-material/Diversity3Rounded";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import MonetizationOnRounded from "@mui/icons-material/MonetizationOnRounded";
import RocketLaunchRounded from "@mui/icons-material/RocketLaunchRounded";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { ReactElement } from "react";
import careersData from "../../data/pages/careers.json";
import { CardsSection } from "../../components/CardsSection";
import { HeroComponent } from "../../components/HeroComponent";
import type { MdaiCardProps } from "../../components/MdaiCard";
import { MdaiDivider } from "../../components/MdaiDivider";
import type { CareersPageTemplate } from "../../types/careersPageTemplate";
import { BentoImages } from "./BentoImages";
import { OpenRoles } from "./OpenRoles";

const typedCareersData = careersData as CareersPageTemplate;

const CULTURE_CARD_ICONS = [
  ComputerIcon,
  RocketLaunchRounded,
  Diversity3Rounded,
] as const;

const BENEFIT_CARD_ICONS = [
  MonetizationOnRounded,
  MedicalServicesIcon,
  BeachAccessIcon,
] as const;

const cultureCards: MdaiCardProps[] = typedCareersData.cultureCards.map(
  (card, index) => ({
    Icon: CULTURE_CARD_ICONS[index] ?? Diversity3Rounded,
    title: card.title,
    body: card.body,
  })
);

const benefitsCards: MdaiCardProps[] = typedCareersData.benefitsCards.map(
  (card, index) => ({
    Icon: BENEFIT_CARD_ICONS[index] ?? BeachAccessIcon,
    title: card.title,
    body: card.body,
  })
);

export default function CareersPage(): ReactElement {
  return (
    <Stack
      sx={{
        width: "100%",
        height: "100%",
        flexDirection: "column",
        alignItems: "center",
        pt: "50px",
        px: { sm: 0, md: "40px" },
        pb: "48px",
      }}
      gap={6}
    >
      <Stack alignItems={"center"}>
        <HeroComponent
          title={typedCareersData.hero.title}
          titleVariant="h1"
          align="center"
          divider={false}
          buttons={
            typedCareersData.hero.ctaText && typedCareersData.hero.ctaHref
              ? [{ text: typedCareersData.hero.ctaText, href: typedCareersData.hero.ctaHref }]
              : []
          }
        />
        <Stack gap={5} alignItems="center">
          <BentoImages />
          <Stack gap={2} maxWidth={800}>
            {typedCareersData.introText.map((paragraph, index) => (
              <Typography
                key={`careers-intro-${index.toString()}`}
                variant="body1"
                textAlign="center"
              >
                {paragraph}
              </Typography>
            ))}
          </Stack>
        </Stack>
      </Stack>

      <MdaiDivider />

      <CardsSection
        title={typedCareersData.cultureSection.title}
        cards={cultureCards}
        cardsPerRow={{ lg: 3 }}
      />
      <CardsSection
        title={typedCareersData.benefitsSection.title}
        cards={benefitsCards}
        cardsPerRow={{ lg: 3 }}
      />

      <MdaiDivider />

      <Stack width="100%" gap={0}>
        <Typography id="open-positions" variant="h3" textAlign="center">
          {typedCareersData.openPositionsSection.title}
        </Typography>
        <OpenRoles />
      </Stack>
    </Stack>
  );
}
